import React, { useState, useEffect, useMemo } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchNoc, updateNocStatus, issueNocCertificate, fetchNocCertificateUrl } from "../api/api";

const PURPOSE_LABELS = {
  construction: "Construction",
  bank_loan: "Bank Loan",
  plot_transfer: "Plot Transfer",
  other: "Other Purpose",
};

const STATUS_LABELS = {
  submitted: "Submitted",
  doc_verification: "Document Verification",
  legal_review: "Legal Review",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_STYLES = {
  submitted: { background: "#fef3c7", color: "#92400e" },
  doc_verification: { background: "#dbeafe", color: "#1e40af" },
  legal_review: { background: "#dbeafe", color: "#1e40af" },
  approved: { background: "#dcfce7", color: "#166534" },
  rejected: { background: "#fee2e2", color: "#991b1b" },
};

const FILTERS = [
  { label: "Pending review", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "All", value: "all" },
];

function purposeLabel(purpose) {
  return PURPOSE_LABELS[purpose] || purpose;
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function NOC() {

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filter, setFilter] = useState("pending");
  const [selectedId, setSelectedId] = useState(null);

  const [nextStatus, setNextStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [certificateFile, setCertificateFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchNoc();
      setRequests(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status !== "approved" && r.status !== "rejected").length,
    [requests]
  );

  const filteredRequests = useMemo(() => {
    if (filter === "all") return requests;
    if (filter === "pending") return requests.filter((r) => r.status !== "approved" && r.status !== "rejected");
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  const selectedRequest = requests.find((r) => r.id === selectedId) || null;

  const selectRequest = (req) => {
    setSelectedId(req.id);
    setNextStatus(req.status);
    setAdminNotes(req.admin_notes || "");
    setCertificateFile(null);
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;
    setBusy(true);
    setError("");
    try {
      const updated = await updateNocStatus(selectedRequest.id, nextStatus, adminNotes);
      setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleIssueCertificate = async () => {
    if (!selectedRequest || !certificateFile) return;
    setBusy(true);
    setError("");
    try {
      const updated = await issueNocCertificate(selectedRequest.id, certificateFile, adminNotes);
      setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setNextStatus(updated.status);
      setCertificateFile(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleViewCertificate = async () => {
    if (!selectedRequest) return;
    setDownloading(true);
    try {
      const url = await fetchNocCertificateUrl(selectedRequest.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div className="noc-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>NOC Requests</h1>
          <div className="pending-badge">{pendingCount} pending</div>
        </div>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        <div className="noc-tabs" style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={filter === f.value ? "active-tab" : ""}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && <p>Loading…</p>}

        {!loading && (
          <div className="noc-table-wrapper">
            <table className="noc-table">
              <thead>
                <tr>
                  <th>Request #</th>
                  <th>Plot</th>
                  <th>Customer</th>
                  <th>Purpose</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredRequests.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: "15px" }}>No requests match this filter.</td></tr>
                )}

                {filteredRequests.map((r) => (
                  <tr key={r.id}>
                    <td>{r.request_number}</td>
                    <td>{r.plot_number || "—"}</td>
                    <td>{r.owner_name || "—"}</td>
                    <td>{purposeLabel(r.purpose)}</td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ ...(STATUS_STYLES[r.status] || {}), padding: "4px 10px", borderRadius: "14px", fontSize: "12px", fontWeight: 600 }}
                      >
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td>
                      <button className="view-btn" onClick={() => selectRequest(r)}>
                        Process
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedRequest && (
          <div className="noc-details-card" style={{ marginTop: "20px" }}>

            <div className="details-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0 }}>
                {selectedRequest.request_number} • Plot {selectedRequest.plot_number || "—"}
              </h2>
              <span
                className="status-badge"
                style={{ ...(STATUS_STYLES[selectedRequest.status] || {}), padding: "4px 10px", borderRadius: "14px", fontSize: "12px", fontWeight: 600 }}
              >
                {STATUS_LABELS[selectedRequest.status] || selectedRequest.status}
              </span>
            </div>

            <p style={{ color: "#666" }}>
              {selectedRequest.owner_name || "—"} · Purpose: {purposeLabel(selectedRequest.purpose)} · Submitted {new Date(selectedRequest.created_at).toLocaleDateString()}
            </p>

            {selectedRequest.certificate_url && (
              <button className="view-btn" onClick={handleViewCertificate} disabled={downloading}>
                {downloading ? "…" : "⬇ View / Download Issued Certificate"}
              </button>
            )}

            <label style={{ display: "block", fontWeight: 600, marginTop: "16px", marginBottom: "6px" }}>
              Admin Notes
            </label>
            <textarea
              rows="3"
              style={{ width: "100%" }}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Internal or customer-facing notes about this request…"
            />

            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "12px", flexWrap: "wrap" }}>
              <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)}>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <button className="progress-btn" onClick={handleUpdateStatus} disabled={busy}>
                {busy ? "…" : "Update Status"}
              </button>
            </div>

            <div style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "16px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
                Issue Certificate
              </label>
              <p style={{ fontSize: "13px", color: "#666", marginTop: 0 }}>
                Uploading a certificate automatically marks this request Approved.
              </p>
              <input type="file" accept=".pdf,image/*" onChange={(e) => setCertificateFile(e.target.files?.[0] || null)} />
              <button
                className="approve-btn"
                style={{ marginLeft: "10px" }}
                onClick={handleIssueCertificate}
                disabled={busy || !certificateFile}
              >
                {busy ? "…" : "✓ Upload & Approve"}
              </button>
            </div>

          </div>
        )}

      </div>

    </div>

  );

}

export default NOC;
